import { safelyParseJSON } from './json';

/**
 * Extract messages from OpenInference flattened attributes.
 * Handles both direct array format and flattened dot-notation format.
 *
 * OpenInference spec: https://github.com/Arize-ai/openinference/blob/main/spec/semantic_conventions.md
 *
 * Flattened format example:
 *   llm.input_messages.0.message.role = "user"
 *   llm.input_messages.0.message.content = "Hello"
 *   llm.input_messages.1.message.role = "assistant"
 *   llm.input_messages.1.message.content = "Hi there"
 *
 * @param {Object|string} attributes - Span attributes (object or JSON string)
 * @param {string} messageType - Either 'input' or 'output'
 * @returns {Array<{role: string, content: string, tool_calls?: Array}>} Array of message objects
 */
export function extractMessages(attributes, messageType) {
  if (!attributes) return [];

  // Parse attributes if string
  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return [];

  // Backend now returns nested structure: attrs.llm.input_messages / attrs.llm.output_messages
  const llmAttrs = attrs.llm || {};
  const messagesKey = messageType === 'input' ? 'input_messages' : 'output_messages';
  const messages = llmAttrs[messagesKey];

  // Check for nested structure (backend unflattened)
  if (Array.isArray(messages)) {
    return messages
      .map(obj => obj.message)
      .filter(Boolean)
      .filter(msg => {
        if (!msg.role) return false;
        // Keep messages with content, contents array, or tool calls
        return msg.content || (msg.contents && msg.contents.length > 0) || (msg.tool_calls && msg.tool_calls.length > 0);
      })
      .map(msg => {
        // Normalize content: extract text from contents array if present
        let content = msg.content;
        if (!content && msg.contents && Array.isArray(msg.contents)) {
          // Extract text from contents array (e.g., contents[0].message_content.text)
          const textContent = msg.contents
            .map(c => c.message_content?.text || c.text || '')
            .filter(Boolean)
            .join('\n');
          content = textContent || JSON.stringify(msg.contents);
        }

        return {
          role: msg.role,
          content: content || '',
          tool_calls: msg.tool_calls ? extractToolCallsFromNested(msg.tool_calls) : null
        };
      });
  }

  // Fallback: Extract from flattened dot-notation format (backward compatibility)
  const prefix = messageType === 'input' ? 'llm.input_messages' : 'llm.output_messages';
  return extractFromFlattenedAttributes(attrs, prefix);
}

/**
 * Extract tool calls from nested structure
 * @param {Array} toolCallsArray - Array of {tool_call: {...}} objects
 * @returns {Array<{id?: string, function: {name: string, arguments: string}}>}
 */
function extractToolCallsFromNested(toolCallsArray) {
  if (!Array.isArray(toolCallsArray)) return [];

  return toolCallsArray
    .map(obj => obj.tool_call)
    .filter(Boolean)
    .filter(tc => tc.function?.name)
    .map(tc => ({
      id: tc.id || null,
      function: {
        name: tc.function.name,
        arguments: tc.function.arguments || '{}'
      }
    }));
}

/**
 * Extract messages from flattened OpenInference attributes.
 * Dynamically discovers message indices without assumptions about depth.
 *
 * @param {Object} attributes - Parsed attributes object
 * @param {string} prefix - Attribute prefix (e.g., 'llm.input_messages')
 * @returns {Array<{role: string, content: string, tool_calls?: Array}>}
 */
function extractFromFlattenedAttributes(attributes, prefix) {
  // Find all keys that match the prefix pattern
  const messageKeys = Object.keys(attributes).filter(key => key.startsWith(prefix + '.'));

  if (messageKeys.length === 0) return [];

  // Extract unique message indices using regex
  const indices = [...new Set(
    messageKeys
      .map(key => {
        const match = key.match(new RegExp(`^${escapeRegex(prefix)}\\.(\\d+)\\.`));
        return match ? parseInt(match[1]) : null;
      })
      .filter(idx => idx !== null)
  )].sort((a, b) => a - b);

  // Build message objects for each index
  return indices.map(index => {
    const messagePrefix = `${prefix}.${index}.message`;
    const role = attributes[`${messagePrefix}.role`];
    const content = attributes[`${messagePrefix}.content`];

    // Extract tool calls if present
    const toolCalls = extractToolCalls(attributes, `${prefix}.${index}.message.tool_calls`);

    return {
      role: role || 'unknown',
      content: content || '',
      tool_calls: toolCalls.length > 0 ? toolCalls : null
    };
  }).filter(msg => msg.role && msg.content);
}

/**
 * Extract tool calls from flattened attributes.
 * Handles the pattern: llm.output_messages.0.message.tool_calls.0.tool_call.*
 *
 * @param {Object} attributes - Parsed attributes object
 * @param {string} prefix - Tool calls prefix
 * @returns {Array<{id?: string, function: {name: string, arguments: string}}>}
 */
function extractToolCalls(attributes, prefix) {
  const toolCallKeys = Object.keys(attributes).filter(key => key.startsWith(prefix + '.'));

  if (toolCallKeys.length === 0) return [];

  // Extract unique tool call indices
  const indices = [...new Set(
    toolCallKeys
      .map(key => {
        const match = key.match(new RegExp(`^${escapeRegex(prefix)}\\.(\\d+)\\.`));
        return match ? parseInt(match[1]) : null;
      })
      .filter(idx => idx !== null)
  )].sort((a, b) => a - b);

  return indices.map(index => {
    const tcPrefix = `${prefix}.${index}.tool_call`;
    return {
      id: attributes[`${tcPrefix}.id`] || null,
      function: {
        name: attributes[`${tcPrefix}.function.name`] || '',
        arguments: attributes[`${tcPrefix}.function.arguments`] || '{}'
      }
    };
  }).filter(tc => tc.function.name);
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract input and output messages from span attributes.
 * Convenience function that returns both input and output messages.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {{input: Array, output: Array}}
 */
export function extractInputOutputMessages(attributes) {
  const input = extractMessages(attributes, 'input');
  let output = extractMessages(attributes, 'output');

  // Fallback: Some frameworks (e.g. Mastra) embed tool calls inside output.value
  // as {text: "", toolCalls: [{toolCallId, toolName, args}]} instead of using
  // the OpenInference llm.output_messages.*.message.tool_calls.* convention.
  // If we got output messages but none have tool_calls, check output.value for them.
  if (output.length > 0 && !output.some(msg => msg.tool_calls?.length > 0)) {
    const toolCalls = _extractToolCallsFromOutputValue(attributes);
    if (toolCalls.length > 0) {
      // Attach tool calls to the first assistant message in the output
      const assistantMsg = output.find(msg => msg.role === 'assistant');
      if (assistantMsg) {
        assistantMsg.tool_calls = toolCalls;
      }
    }
  }

  // Fallback: If no output messages were found at all, check output.value
  // for tool calls (some frameworks only emit output.value, not llm.output_messages)
  if (output.length === 0) {
    const toolCalls = _extractToolCallsFromOutputValue(attributes);
    if (toolCalls.length > 0) {
      output = [{
        role: 'assistant',
        content: '',
        tool_calls: toolCalls
      }];
    }
  }

  return { input, output };
}

/**
 * Extract tool calls from the raw LLM response embedded in output.value.
 * Handles frameworks like Mastra that store tool call results as:
 *   {"text": "", "toolCalls": [{"toolCallId": "...", "toolName": "...", "args": {...}}]}
 * instead of using the OpenInference llm.output_messages.*.message.tool_calls.* convention.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {Array<{id: string, function: {name: string, arguments: string}}>}
 */
function _extractToolCallsFromOutputValue(attributes) {
  if (!attributes) return [];

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return [];

  // output.value may be nested (attrs.output.value) or flat (attrs['output.value'])
  const outputValueRaw = attrs.output?.value || attrs['output.value'];
  if (!outputValueRaw) return [];

  const outputValue = typeof outputValueRaw === 'string'
    ? safelyParseJSON(outputValueRaw)
    : outputValueRaw;

  if (!outputValue || !Array.isArray(outputValue.toolCalls)) return [];

  return outputValue.toolCalls
    .filter(tc => tc.toolName || tc.function?.name)
    .map(tc => ({
      id: tc.toolCallId || tc.id || null,
      function: {
        // Normalize Mastra's toolName to OpenInference's function.name
        name: tc.toolName || tc.function?.name || '',
        arguments: typeof tc.args === 'string'
          ? tc.args
          : JSON.stringify(tc.args || tc.function?.arguments || {})
      }
    }));
}

/**
 * Extract LLM tools from OpenInference attributes.
 * Handles llm.tools.*.tool.json_schema pattern.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {Array<{name: string, description?: string, json_schema: Object}>}
 */
export function extractLLMTools(attributes) {
  if (!attributes) return [];

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return [];

  // Backend now returns nested structure: attrs.llm.tools
  const llmAttrs = attrs.llm || {};
  const tools = llmAttrs.tools;

  // Check for nested structure (backend unflattened)
  if (Array.isArray(tools)) {
    return tools
      .map(obj => obj.tool)
      .filter(Boolean)
      .filter(tool => tool.json_schema)
      .map(tool => ({
        json_schema: typeof tool.json_schema === 'string'
          ? safelyParseJSON(tool.json_schema)
          : tool.json_schema
      }))
      .filter(tool => tool.json_schema);
  }

  // Fallback: Extract from flattened format (backward compatibility)
  const prefix = 'llm.tools';
  const toolKeys = Object.keys(attrs).filter(key => key.startsWith(prefix + '.'));

  if (toolKeys.length > 0) {
    const indices = [...new Set(
      toolKeys
        .map(key => {
          const match = key.match(/^llm\.tools\.(\d+)\./);
          return match ? parseInt(match[1]) : null;
        })
        .filter(idx => idx !== null)
    )].sort((a, b) => a - b);

    const result = indices.map(index => {
      const toolPrefix = `${prefix}.${index}.tool`;
      const jsonSchema = attrs[`${toolPrefix}.json_schema`];

      return {
        json_schema: typeof jsonSchema === 'string' ? safelyParseJSON(jsonSchema) : jsonSchema
      };
    }).filter(tool => tool.json_schema);

    if (result.length > 0) return result;
  }

  // Fallback: Some frameworks (e.g. Mastra) embed tool definitions inside input.value
  // as part of the raw LLM request payload rather than as separate llm.tools.* attributes.
  // Extract them from input.value.tools[] and normalize to OpenInference json_schema format.
  return _extractToolsFromInputValue(attrs);
}

/**
 * Extract tool definitions from the raw LLM request embedded in input.value.
 * Handles frameworks like Mastra that store the full LLM API request payload
 * (including tools array) as a JSON string in the input.value attribute
 * instead of using the OpenInference llm.tools.N.tool.json_schema convention.
 *
 * Expected input.value format:
 *   {"model": "...", "tools": [{"type": "function", "name": "...", ...}], ...}
 *
 * @param {Object} attrs - Parsed span attributes
 * @returns {Array<{json_schema: Object}>}
 */
function _extractToolsFromInputValue(attrs) {
  // input.value may be nested (attrs.input.value) or flat (attrs['input.value'])
  const inputValueRaw = attrs.input?.value || attrs['input.value'];
  if (!inputValueRaw) return [];

  const inputValue = typeof inputValueRaw === 'string'
    ? safelyParseJSON(inputValueRaw)
    : inputValueRaw;

  if (!inputValue || !Array.isArray(inputValue.tools)) return [];

  return inputValue.tools
    .filter(tool => tool.name || tool.function?.name)
    .map(tool => ({
      // Wrap the raw tool definition as json_schema to match OpenInference format
      // so downstream components (ToolSchemasChevron) can render it consistently
      json_schema: tool
    }));
}

/**
 * Extract LLM prompt template from OpenInference attributes.
 * Handles llm.prompt_template.template and llm.prompt_template.variables.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {{template: string, variables: Object} | null}
 */
export function extractPromptTemplate(attributes) {
  if (!attributes) return null;

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return null;

  // Backend now returns nested structure: attrs.llm.prompt_template
  const llmAttrs = attrs.llm || {};
  const promptTemplate = llmAttrs.prompt_template;

  if (promptTemplate && promptTemplate.template) {
    return {
      template: promptTemplate.template,
      variables: typeof promptTemplate.variables === 'string'
        ? safelyParseJSON(promptTemplate.variables)
        : promptTemplate.variables
    };
  }

  // Fallback: Extract from flattened format (backward compatibility)
  const template = attrs['llm.prompt_template.template'];
  const variables = attrs['llm.prompt_template.variables'];

  if (!template) return null;

  return {
    template,
    variables: typeof variables === 'string' ? safelyParseJSON(variables) : variables
  };
}

/**
 * Extract LLM prompts from OpenInference attributes.
 * Handles llm.prompts.*.prompt.text pattern per OpenInference spec.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {Array<string>}
 */
export function extractPrompts(attributes) {
  if (!attributes) return [];

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return [];

  // Backend now returns nested structure: attrs.llm.prompts
  const llmAttrs = attrs.llm || {};
  const prompts = llmAttrs.prompts;

  // Check for nested structure (backend unflattened)
  if (Array.isArray(prompts)) {
    return prompts
      .map(obj => obj.prompt?.text)
      .filter(text => typeof text === 'string' && text.length > 0);
  }

  // Fallback: Extract from flattened format (backward compatibility)
  const prefix = 'llm.prompts';
  const promptKeys = Object.keys(attrs).filter(key => key.startsWith(prefix + '.'));

  if (promptKeys.length === 0) return [];

  const indices = [...new Set(
    promptKeys
      .map(key => {
        const match = key.match(/^llm\.prompts\.(\d+)\.prompt\.text$/);
        return match ? parseInt(match[1]) : null;
      })
      .filter(idx => idx !== null)
  )].sort((a, b) => a - b);

  return indices
    .map(index => attrs[`${prefix}.${index}.prompt.text`])
    .filter(prompt => typeof prompt === 'string' && prompt.length > 0);
}

/**
 * Extract LLM invocation parameters from OpenInference attributes.
 * Handles llm.invocation_parameters (JSON string).
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {Object | null}
 */
export function extractInvocationParameters(attributes) {
  if (!attributes) return null;

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return null;

  // Backend now returns nested structure: attrs.llm.invocation_parameters
  const llmAttrs = attrs.llm || {};
  const invocationParams = llmAttrs.invocation_parameters;

  if (invocationParams) {
    const parsed = typeof invocationParams === 'string'
      ? safelyParseJSON(invocationParams)
      : invocationParams;

    if (parsed && Object.keys(parsed).length > 0) return parsed;
  }

  // Fallback: Extract from flattened format (backward compatibility)
  const flatInvocationParams = attrs['llm.invocation_parameters'];

  if (flatInvocationParams) {
    const parsed = typeof flatInvocationParams === 'string'
      ? safelyParseJSON(flatInvocationParams)
      : flatInvocationParams;

    if (parsed && Object.keys(parsed).length > 0) return parsed;
  }

  // Fallback: Some frameworks (e.g. Mastra) embed invocation parameters inside input.value
  // as part of the raw LLM request payload (model, temperature, tool_choice, etc.)
  // instead of using the OpenInference llm.invocation_parameters convention.
  // Extract recognized LLM config fields from input.value when standard attributes are missing.
  const inputValueRaw = attrs.input?.value || attrs['input.value'];
  if (!inputValueRaw) return null;

  const inputValue = typeof inputValueRaw === 'string'
    ? safelyParseJSON(inputValueRaw)
    : inputValueRaw;

  if (!inputValue || typeof inputValue !== 'object') return null;

  // Pick known invocation parameter fields from the raw request payload
  const knownParams = ['model', 'temperature', 'max_tokens', 'top_p', 'top_k',
    'frequency_penalty', 'presence_penalty', 'stop', 'tool_choice', 'response_format',
    'seed', 'stream'];
  const extracted = {};
  for (const key of knownParams) {
    if (inputValue[key] !== undefined) {
      extracted[key] = inputValue[key];
    }
  }

  return Object.keys(extracted).length > 0 ? extracted : null;
}

/**
 * Extract tool attributes from OpenInference attributes.
 * Handles tool.name, tool.description, tool.parameters.
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {{name: string | null, description: string | null, parameters: Object | null}}
 */
export function extractToolAttributes(attributes) {
  if (!attributes) return { name: null, description: null, parameters: null };

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return { name: null, description: null, parameters: null };

  // Standard OpenInference tool attributes
  const name = attrs['tool.name'] || attrs.tool?.name || null;
  const description = attrs['tool.description'] || attrs.tool?.description || null;
  const parametersRaw = attrs['tool.parameters'] || attrs.tool?.parameters;

  const parameters = parametersRaw
    ? (typeof parametersRaw === 'string' ? safelyParseJSON(parametersRaw) : parametersRaw)
    : null;

  // Fallback for parameters: Some frameworks (e.g. Mastra) don't emit tool.parameters
  // but the tool schema may be available inside the input.value of a parent/sibling LLM span.
  // If we have a tool name but no parameters, try to extract from input.value.tools[].
  if (name && !parameters) {
    const inputValueRaw = attrs.input?.value || attrs['input.value'];
    if (inputValueRaw) {
      const inputValue = typeof inputValueRaw === 'string'
        ? safelyParseJSON(inputValueRaw)
        : inputValueRaw;

      if (inputValue && Array.isArray(inputValue.tools)) {
        const matchingTool = inputValue.tools.find(t =>
          t.name === name || t.function?.name === name
        );
        if (matchingTool) {
          return {
            name,
            description: description || matchingTool.description || matchingTool.function?.description || null,
            parameters: matchingTool.parameters || matchingTool.function?.parameters || null
          };
        }
      }
    }
  }

  return { name, description, parameters };
}

/**
 * Extract embedding attributes from OpenInference attributes.
 * Handles embedding.model_name and embedding.embeddings array.
 *
 * NOTE: Vectors are replaced with placeholders by backend (e.g., "<1536 dimensional vector>"),
 * so we only extract text. Frontend should never display raw vectors (1536+ floats).
 *
 * OpenInference spec: embedding.embeddings.*.embedding.text
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {{model_name: string|null, embeddings: Array<{text: string}>}}
 */
export function extractEmbeddingAttributes(attributes) {
  if (!attributes) return { model_name: null, embeddings: [] };

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') return { model_name: null, embeddings: [] };

  // Backend returns nested structure: attrs.embedding (after unflatten_attributes)
  const embedding = attrs.embedding || {};

  return {
    model_name: embedding.model_name || null,
    embeddings: Array.isArray(embedding.embeddings)
      ? embedding.embeddings
          .map(obj => obj.embedding)
          .filter(Boolean)
          .map(emb => ({
            text: emb.text || '',
            // NOTE: Do NOT extract vector - backend replaces it with placeholder
            // Frontend should never display vectors (arrays of 1536+ floats)
          }))
          .filter(emb => emb.text) // Only include embeddings with text
      : []
  };
}

/**
 * Extract reranker attributes from OpenInference attributes.
 * Handles reranker.query, model_name, top_k, and input/output documents.
 *
 * OpenInference spec: reranker.input_documents.*.document.*
 *
 * @param {Object|string} attributes - Span attributes
 * @returns {{query: string|null, model_name: string|null, top_k: number|null, input_documents: Array, output_documents: Array}}
 */
export function extractRerankerAttributes(attributes) {
  if (!attributes) {
    return {
      query: null,
      model_name: null,
      top_k: null,
      input_documents: [],
      output_documents: []
    };
  }

  const attrs = typeof attributes === 'string'
    ? safelyParseJSON(attributes)
    : attributes;

  if (!attrs || typeof attrs !== 'object') {
    return {
      query: null,
      model_name: null,
      top_k: null,
      input_documents: [],
      output_documents: []
    };
  }

  // Backend returns nested structure: attrs.reranker (after unflatten_attributes)
  const reranker = attrs.reranker || {};

  return {
    query: reranker.query || null,
    model_name: reranker.model_name || null,
    top_k: reranker.top_k || null,
    input_documents: Array.isArray(reranker.input_documents)
      ? reranker.input_documents.map(obj => obj.document).filter(Boolean)
      : [],
    output_documents: Array.isArray(reranker.output_documents)
      ? reranker.output_documents.map(obj => obj.document).filter(Boolean)
      : []
  };
}
